// ---------------------------------------------------------------------------
// Finance model (single source of truth)
//
// Plain-English glossary surfaced across the app:
//   Budget (BAC)   the approved ceiling we planned to spend
//   Allocated      how much of the budget is committed / assigned out
//   Spent (AC)     money actually spent to date (actuals)
//   ETC            Estimate to Complete  = cost of the work still remaining
//   EAC            Estimate at Completion = forecast TOTAL cost when finished
//   Variance       Budget − EAC  (how the forecast compares to the ceiling)
//
// Allocation-based forecast (chosen model):
//   EAC      = Allocated            (we expect to spend what we've committed)
//   ETC      = Allocated − Spent    (what's left to spend of the commitment)
//   Variance = Budget − EAC
//   Favorable   → Variance ≥ 0  (under budget)   → green
//   Unfavorable → Variance < 0  (over budget)    → red
//
// EAC can never be less than what's already spent, so it is clamped to Spent.
// If no allocation figure exists, Allocated falls back to Spent.
// ---------------------------------------------------------------------------

export interface FinanceInput {
  budget?: number | null;
  allocated?: number | null;
  spent?: number | null;
}

export interface FinanceResult {
  budget: number;
  allocated: number;
  spent: number;
  etc: number;
  eac: number;
  variance: number;
  /** Variance as a share of budget (signed). */
  variancePct: number;
  /** Spent ÷ Budget. */
  utilizationPct: number;
  isFavorable: boolean;
  /** Convenience for StatCard / chips. */
  tone: 'favorable' | 'unfavorable' | 'neutral';
  varianceLabel: 'Favorable' | 'Unfavorable' | 'On budget';
}

export const computeFinance = (input: FinanceInput): FinanceResult => {
  const budget = Math.max(0, Number(input.budget) || 0);
  const spent = Math.max(0, Number(input.spent) || 0);
  const allocatedRaw = Number(input.allocated);
  const allocated = Number.isFinite(allocatedRaw) && allocatedRaw > 0 ? allocatedRaw : spent;

  const eac = Math.max(spent, allocated);
  const etc = Math.max(0, eac - spent);
  const variance = budget - eac;
  const variancePct = budget > 0 ? (variance / budget) * 100 : 0;
  const utilizationPct = budget > 0 ? (spent / budget) * 100 : 0;

  const isFavorable = variance >= 0;
  const tone: FinanceResult['tone'] = variance === 0 ? 'neutral' : isFavorable ? 'favorable' : 'unfavorable';
  const varianceLabel = variance === 0 ? 'On budget' : isFavorable ? 'Favorable' : 'Unfavorable';

  return { budget, allocated, spent, etc, eac, variance, variancePct, utilizationPct, isFavorable, tone, varianceLabel };
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Compact currency: $1.2M, $940K, $0. Good for stat cards and axis ticks. */
export const formatCurrencyShort = (n: number | null | undefined, currency = 'USD'): string => {
  const v = Number(n) || 0;
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const sym = currencySymbol(currency);
  if (abs >= 1_000_000_000) return `${sign}${sym}${trim(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${sign}${sym}${trim(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}${sym}${trim(abs / 1_000)}K`;
  return `${sign}${sym}${Math.round(abs)}`;
};

/** Full currency with grouping: $1,240,000. */
export const formatCurrency = (n: number | null | undefined, currency = 'USD'): string => {
  const v = Number(n) || 0;
  const code = typeof currency === 'string' && currency ? currency : 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currencySymbol(code)}${Math.round(v).toLocaleString()}`;
  }
};

/** Signed compact currency for variances: +$20K / −$8K. */
export const formatSignedCurrencyShort = (n: number | null | undefined, currency = 'USD'): string => {
  const v = Number(n) || 0;
  if (v === 0) return formatCurrencyShort(0, currency);
  return `${v > 0 ? '+' : '−'}${formatCurrencyShort(Math.abs(v), currency)}`;
};

export const formatPercent = (n: number | null | undefined, digits = 0): string => {
  const v = Number(n) || 0;
  return `${v.toFixed(digits)}%`;
};

export const formatSignedPercent = (n: number | null | undefined, digits = 0): string => {
  const v = Number(n) || 0;
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits)}%`;
};

const trim = (n: number): string => {
  // 1 decimal place, but drop a trailing .0
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

const currencySymbol = (currency: string): string => {
  // Defensive: charting libs sometimes call formatters as fn(value, index),
  // so `currency` can arrive as a number. Fall back to USD unless it's a string.
  const code = typeof currency === 'string' && currency ? currency : 'USD';
  switch (code.toUpperCase()) {
    case 'USD':
    case 'CAD':
    case 'AUD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$';
  }
};
